/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Warren Dowey
 */

import { NgModule } from '@angular/core';
import { RouterModule, NoPreloading } from '@angular/router';
import { AuthenticationGuard } from '@app/services/authentication/authentication.guard';
import { CaseDetailsGuard } from '@app/guards/case-details.guard';
import { ImageViewerGuard } from '@app/guards/image-viewer.guard';
import { BaseLayoutComponent } from './components/layouts/base-layout/base-layout.component';
import { BlankLayoutComponent } from '@app/components/layouts/blank-layout/blank-layout.component';

const routes: Array<any> = [
  {
    path: '',
    component: BaseLayoutComponent,
    data: { extraParameter: 'dashboardsMenu' },
    children: [
      {
        path: '',
        // Home or '' now redirects to Login or Dashboard if already logged in
        // loadChildren: './home/home.module#HomeModule',
        // data: {extraParameter: 'dashboardsMenu'}
        redirectTo: '/login',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./components/cases-list/cases-list.module').then(
            (m) => m.CasesListModule
          ),
        data: {
          title: 'Cases',
          breadcrumb: [
            {
              label: 'Cases',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./components/profile/profile.module').then((m) => m.ProfileModule),
        data: {
          title: 'User Profile',
          breadcrumb: [
            {
              label: 'User Profile',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'register',
        loadChildren: () =>
          import('@app/components/register/register.module').then((m) => m.RegisterModule),
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'reset-password',
        loadChildren: () =>
          import('@app/components/reset-password/reset-password.module').then(
            (m) => m.ResetPasswordModule
          ),
      },
      {
        path: 'change-password',
        loadChildren: () =>
          import('@app/components/change-password/change-password.module').then(
            (m) => m.ChangePasswordModule
          ),
        data: {
          title: 'Change Password',
          breadcrumb: [
            {
              label: 'Home',
              url: '/',
            },
            {
              label: 'Change Password',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'app-loader',
        loadChildren: () =>
          import('@app/components/app-loader/app-loader.module').then((m) => m.AppLoaderModule),
        data: {
          title: 'Projects',
          breadcrumb: [
            {
              label: 'Projects',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'data-repo',
        loadChildren: () =>
          import('@app/components/data-repo/data-repo.module').then((m) => m.DataRepoModule),
        data: {
          title: 'Data Repo',
          breadcrumb: [
            {
              label: 'Data Repo',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'user-guides',
        loadChildren: () =>
          import('@app/components/user-guides/user-guides.module').then((m) => m.UserGuidesModule),
        data: {
          title: 'User Guides',
          breadcrumb: [
            {
              label: 'User Guides',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'contact-us',
        loadChildren: () =>
          import('@app/components/contact-us/contact-us.module').then((m) => m.ContactUsModule),
        data: {
          title: 'Contact Us',
          breadcrumb: [
            {
              label: 'Contact Us',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'admin-dashboard',
        loadChildren: () =>
          import('./components/admin-dashboard/admin-dashboard.module').then(
            (m) => m.AdminDashboardModule
          ),
        data: {
          title: 'Admin Dashboard',
          breadcrumb: [
            {
              label: 'Home',
              url: '/',
            },
            {
              label: 'Admin Dashboard',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'admin/admin-user',
        loadChildren: () =>
          import('./components/admin-organisation-users/admin-organisation-users.module').then(
            (m) => m.AdminOrganisationUsersModule
          ),
        data: {
          title: '',
          breadcrumb: [
            {
              label: 'Home',
              url: '/',
            },
            {
              label: 'User Management',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'admin/admin-activity',
        loadChildren: () =>
          import('@app/components/admin-activity/admin-activity.module').then(
            (m) => m.AdminActivityModule
          ),
        data: {
          title: '',
          breadcrumb: [
            {
              label: 'Home',
              url: '/',
            },
            {
              label: 'Activity',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'admin/admin-settings',
        loadChildren: () =>
          import('./components/admin-settings/admin-settings.module').then(
            (m) => m.AdminSettingsModule
          ),
        data: {
          title: '',
          breadcrumb: [
            {
              label: 'Home',
              url: '/',
            },
            {
              label: 'Settings',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'admin/super-admin-dashboard',
        loadChildren: () =>
          import('./components/super-admin-dashboard/super-admin-dashboard.module').then(
            (m) => m.SuperAdminDashboardModule
          ),
        data: {
          title: 'Super Admin Dashboard',
          breadcrumb: [
            {
              label: 'Home',
              url: '/',
            },
            {
              label: 'Super Admin Dashboard',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'cases',
        loadChildren: () =>
          import('./components/cases-list/cases-list.module').then((m) => m.CasesListModule),
        data: {
          title: 'Cases',
          breadcrumb: [
            {
              label: 'Cases',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'unassigned-cases',
        loadChildren: () =>
          import('./components/cases-list/cases-list.module').then((m) => m.CasesListModule),
        data: {
          title: 'Unassigned Cases',
          breadcrumb: [
            {
              label: 'Unassigned Cases',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'my-cases',
        loadChildren: () =>
          import('./components/cases-list/cases-list.module').then((m) => m.CasesListModule),
        data: {
          title: 'My Cases',
          breadcrumb: [
            {
              label: 'My Cases',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'cases/:slug',
        loadChildren: () =>
          import('./components/case-view/case-view.module').then((m) => m.CaseViewModule),
        data: {
          title: 'Case',
          breadcrumb: [
            {
              label: 'Cases',
              url: '/cases',
            },
            {
              label: '{{dynamicText}}',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard, CaseDetailsGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'cases/:slug/viewer',
        loadChildren: () =>
          import('./components/leaflet-viewer/leaflet-viewer.module').then(
            (m) => m.LeafletViewerModule
          ),
        data: {
          title: 'Cases',
          breadcrumb: [
            {
              label: 'Cases',
              url: '/cases',
            },
            {
              label: '{{dynamicText}}',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard, ImageViewerGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'images',
        loadChildren: () =>
          import('@app/components/image-list/image-list.module').then((m) => m.ImageListModule),
        data: {
          title: 'Images',
          breadcrumb: [
            {
              label: 'Images',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
      {
        path: 'images/:slug/viewer',
        loadChildren: () =>
          import('@base/projects/image-dx/src/app/components/shared/unassigned-image-viewer/unassigned-image-viewer.module').then(
            (m) => m.UnassignedImageViewerModule
          ),
        data: {
          title: 'Viewer',
          breadcrumb: [
            {
              label: 'Images',
              url: '/images',
            },
            {
              label: '{{dynamicText}}',
              url: '',
            },
            {
              label: 'Viewer',
              url: '',
            },
          ],
        },
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
      },
    ],
  },
  {
    path: '',
    component: BlankLayoutComponent,
    canActivate: [AuthenticationGuard],
    canActivateChild: [AuthenticationGuard],
    children: [
      {
        path: 'login',
        loadChildren: () => import('@app/components/login/login.module').then((m) => m.LoginModule),
      },
      {
        path: 'forgot-password',
        loadChildren: () =>
          import('@app/components/forgot-password/forgot-password.module').then(
            (m) => m.ForgotPasswordModule
          ),
      },
      {
        path: 'mfa-entry',
        loadChildren: () =>
          import('@app/components/mfa-entry/mfa-entry.module').then((m) => m.MfaEntryModule),
      },
      {
        path: 'new-password',
        loadChildren: () =>
          import('@app/components/new-password/new-password.module').then(
            (m) => m.NewPasswordModule
          ),
      },
      {
        path: '**',
        loadChildren: () => import('@app/components/login/login.module').then((m) => m.LoginModule),
      }
    ],
  },
  {
    path: '',
    component: BlankLayoutComponent,
    children: [
      {
        path: 'maintenance',
        loadChildren: () =>
          import('@app/components/maintenance/maintenance.module').then((m) => m.MaintenanceModule),
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
    preloadingStrategy: NoPreloading,
    enableTracing: false
}),
  ],
  exports: [RouterModule],
  providers: [],
})
export class AppRoutingModule {}
